import { MemberExtended } from "@nova/schemas"
import { FunctionComponent } from "react"
import { AddOnIcon } from "plugins/AddonIcon"
import { LicenseBadge } from "@nova/ds-component-license-badge"
import * as Icons from "@nova/component-icons"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import classNames from "classnames"
import { PilotCurrencyBadge } from "features/pilot-tracking/currency/PilotCurrencyBadge"
import { CurrencyPolicyStatus } from "features/pilot-tracking/currency/usePilotCurrencyStatuses"

interface PilotCardProps {
    user: MemberExtended
    currencyStatuses?: CurrencyPolicyStatus[]
    inactive?: boolean
}

export const PilotCard: FunctionComponent<PilotCardProps> = ({
    user,
    currencyStatuses,
    inactive,
}) => {
    const name = user.fullName ?? `${user.givenName} ${user.familyName}`
    return (
        <div
            data-active={!inactive}
            className="group/pilot-card flex flex-col ring-1 ring-white-100 hover:ring-2 hover:ring-white-400 rounded-xl bg-white-100 hover:shadow-lg transition-all w-full h-full data-[active=false]:opacity-50"
        >
            <div
                className={classNames(
                    "flex items-center justify-center w-full aspect-square max-h-[213px] relative",
                    {
                        "pt-4 px-4": !!user.profilePhotoS3Key,
                    }
                )}
            >
                <div
                    className={classNames(
                        "w-full h-full relative",
                        user.profilePhotoS3Key ? "rounded-lg" : "rounded-t-xl"
                    )}
                >
                    <div
                        className={classNames(
                            "absolute inset-0 bg-black-1000 z-20 transition-opacity",
                            user.profilePhotoS3Key ? "rounded-lg" : "rounded-t-xl",
                            {
                                "opacity-[45%] group-hover/pilot-card:opacity-30":
                                    !user.profilePhotoS3Key,
                                "opacity-20 group-hover/pilot-card:opacity-0":
                                    !!user.profilePhotoS3Key,
                            }
                        )}
                    />
                    <ProfilePhoto
                        userId={user.id ?? ""}
                        fullName={name}
                        initialsFontSize={30}
                        objectPosition="top"
                        className={user.profilePhotoS3Key ? "rounded-lg" : "rounded-t-xl"}
                    />
                </div>
                {currencyStatuses && (
                    <div className="absolute top-6 left-6 z-30">
                        <PilotCurrencyBadge statuses={currencyStatuses} />
                    </div>
                )}
            </div>
            <div className="flex flex-col gap-3 p-4 h-[106px]">
                <div className="flex flex-col gap-1">
                    <div className="text-white text-[21px] truncate pb-1">{name}</div>
                    {user.title && (
                        <small className="text-gray-300 xs truncate">{user.title}</small>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <LicenseBadge license={user.license.subscriptionType} />

                    <div className="flex items-center gap-2">
                        {Object.values(user.addOns).map(
                            (addOn) =>
                                addOn?.subscriptionType && (
                                    <AddOnIcon
                                        key={addOn.subscriptionId}
                                        addOn={addOn.subscriptionType}
                                    />
                                )
                        )}
                    </div>
                    {!inactive && (
                        <Icons.SmallIcon
                            Icon={Icons.ArrowRight}
                            className="text-white ml-auto opacity-0 group-hover/pilot-card:opacity-100 transition-opacity"
                        />
                    )}
                </div>
            </div>
        </div>
    )
}
